import { delay, exists } from 'base/helpers';
import { Locale } from 'base/ui/locales';
import { normalizeSectionName } from 'base/webflow/page_component';
import { mapLines } from '../../llm_output_parsers';
import { LLMProxyService } from '../../llm_proxy_service';
import { PageState, Project, ProjectState, SectionState } from '../model';
import { LoadApprovedCategories } from './approved_categories';

/** Infers the desired component category based on a section's name and description */
export class SectionToComponentCategoryAi {
  private readonly categoryNameForSection = new Map<
    string,
    Promise<string | undefined>
  >();

  constructor(
    private readonly llm: LLMProxyService,
    private readonly loadApprovedCategories: LoadApprovedCategories,
    private readonly project: Project,
  ) {}

  private cacheKeyForSection(section: SectionState, locale: Locale) {
    return `${locale}__${section.name}___${section.description}`;
  }

  private async inferCategoryNamesForPage(page: PageState, locale: Locale) {
    const resolveCallbacks: ((v: string | undefined) => void)[] = [];
    const rejectCallbacks: ((e: unknown) => void)[] = [];
    const pageSections = resolvePageSectionStates(page, this.project.state);
    let promisesSet = 0;
    for (const section of pageSections) {
      const key = this.cacheKeyForSection(section, locale);
      const promise = new Promise<string | undefined>((resolve, reject) => {
        resolveCallbacks.push(resolve);
        rejectCallbacks.push(reject);
      });
      // Don't persist errors past 1s, so the user can retry.
      promise
        .catch(() => delay(1000))
        .then(() => {
          if (this.categoryNameForSection.get(key) === promise) {
            this.categoryNameForSection.delete(key);
          }
        });

      const existing = this.categoryNameForSection.get(key);
      if (existing == null) {
        this.categoryNameForSection.set(key, promise);
        promisesSet++;
      }
    }

    if (promisesSet === 0) return;
    try {
      const approvedCategories = await this.loadApprovedCategories();
      const llmResponse = this.llm.infer('section-component-1', {
        acceptedCategoryCsv: approvedCategories.all.createCsv(locale),
        inputCsv: pageSections
          .map(
            (s, i) =>
              `${i + 1}|${page.name}|${s.name}|${
                s.description || 'no description'
              }`,
          )
          .join('\n'),
      });

      for await (const line of mapLines(llmResponse)) {
        if (!line.trim()) continue;
        // the header is sometimes repeated:
        if (line.trim() === 'id|page|category id|category') continue;
        try {
          const cells = line.split('|');
          if (cells.length !== 4) throw new Error('Expected 4 cells only');
          const [indexStr, pageName, categoryIdStr, llmCategoryName] = cells;
          const sectionIndex = parseInt(indexStr, 10) - 1;
          if (isNaN(sectionIndex) || page.sections[sectionIndex] == null) {
            throw new Error('Invalid index');
          }
          if (pageName.toLowerCase() !== page.name.toLowerCase()) {
            console.warn(
              `Page name in output "${pageName}" != input "${page.name}"`,
            );
          }
          const categoryIndex = parseInt(categoryIdStr, 10) - 1;
          const category = approvedCategories.all.englishNames[categoryIndex];
          if (isNaN(categoryIndex) || category == null) {
            throw new Error('Invalid category index');
          }
          if (
            normalizeSectionName(category) !==
            normalizeSectionName(llmCategoryName)
          ) {
            console.warn(
              `Output category index (${categoryIdStr}/${category}) != output category name (${llmCategoryName}), taking index`,
            );
          }

          resolveCallbacks[sectionIndex](category);
        } catch (err) {
          console.warn('Could not parse output line', line, err);
        }
      }
      // Resolve all remaining sections:
      resolveCallbacks.map((cb) => cb(undefined));
    } catch (e) {
      rejectCallbacks.forEach((cb) => cb(e));
    }
  }

  readonly getBlocksCategoryForSection = async (
    locale: Locale,
    page: PageState,
    section: SectionState,
  ): Promise<string | undefined> => {
    const category = (await this.loadApprovedCategories()).all.findClosest(
      section.name,
    );
    if (category) return category.name;

    const key = this.cacheKeyForSection(section, locale);
    if (!this.categoryNameForSection.has(key)) {
      this.inferCategoryNamesForPage(page, locale);
    }
    return await this.categoryNameForSection.get(key)!;
  };
}

function resolvePageSectionStates(page: PageState, project: ProjectState) {
  return page.sections
    .map((sref) => {
      return sref.type === 'inline'
        ? sref.value
        : project.globalSections.get(sref.id);
    })
    .filter(exists);
}
